import type { SceneryKind } from '@/config/cities';
import type { ZoneBuilder } from './types';
import { hillLat } from './hills';
import { forwardX, forwardZ } from '@/math/orientation';
import { scatterPeople } from './crowd';
import { hash } from './water';
import {
  ADBUILDING,
  BROWNSTONE,
  FOLIAGE,
  FOLIAGE_AUTUMN,
  GENERIC,
  GLASS,
  HILL_GREEN,
  LONDON_STONE,
  NEON,
  SKYLINE,
  darken,
} from './palettes';
import { NAPOLI_ZONES } from './zones/napoli';
import { TOKYO_ZONES } from './zones/tokyo';
import { RIO_ZONES } from './zones/rio';
import { DAKAR_ZONES } from './zones/dakar';
import { LAGOS_ZONES } from './zones/lagos';
import { MUMBAI_ZONES } from './zones/mumbai';
import { PATAGONIA_ZONES } from './zones/patagonia';

/**
 * scenery/zones.ts — one builder per SceneryKind: the creative heart of each
 * city's roadside. A builder is called once per (12-unit step × road side) and
 * pushes instances into ctx.a; the meshes are assembled afterwards in a handful
 * of draw calls (scenery/builders.ts). NYC + London live here; later cities
 * each get their own module under scenery/zones/.
 *
 * DETERMINISM: a city's look is its seed + the exact SEQUENCE of rng calls
 * below. Don't reorder rng calls inside a zone without expecting that city's
 * layout to reshuffle (it stays valid — just different).
 */

/** The Thames runs along the right-hand side of the road. */
export const RIVER_SIDE = 1;

// ---- New York --------------------------------------------------------------

/**
 * NYC DESIGN NOTES (graphics overhaul, NYC TS round). Everything added here is
 * DISPLAY ONLY and takes its variety from an INDEX HASH, never from `rng` — the
 * city's layout is the exact sequence of rng calls, so detail work must never
 * add, drop or reorder a draw. Where a placement has to move (a pond off the
 * works site, a tree out of the water) the draws still happen and only the
 * RESULT is clamped: the established byte-safe pattern.
 */

/** Deterministic 0..1 from a stable integer key (the builders.ts convention). */
function nycVary(key: number, salt: number): number {
  return hash(key * 7.31 + salt * 131.7);
}

/** A stable integer key for a placement slot: its course arc + side. Two
 *  different buildings can never collide on it, and it is identical on every
 *  client because the track is. */
function slotKey(distance: number, side: -1 | 1): number {
  return Math.round(distance * 4) * 2 + (side > 0 ? 1 : 0);
}

const brownstones: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side }) => {
  if (rng.next() < 0.85) {
    const w = rng.range(7, 12);
    const h = rng.range(5, 13);
    const d = rng.range(8, 14);
    const lat = hw + 3 + w / 2 + rng.range(0, 8);
    const [x, y, z] = at(lat);
    const color = rng.pick(BROWNSTONE);
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
    // Cornice along the roofline + a stone stoop toward the street.
    a.plain.push({ x, y: y + h - 0.5, z, rotY: s.dir, w: w + 0.8, h: 1, d: d + 0.8, color: darken(color, 0.62) });
    const [sx, sy, sz] = at(lat - w / 2 - 0.6);
    a.plain.push({ x: sx, y: sy, z: sz, rotY: s.dir, w: 2.4, h: 1.5, d: 2.4, color: 0xb4b8c0 });
    // The Harlem signatures (M14): a black iron FIRE ESCAPE zig-zagging down
    // the street face, and a wooden WATER TOWER on the taller roofs.
    //
    // NYC TS round — REAL IRONWORK. The escape used to be two solid slabs per
    // level plus a yawed slab standing in for the ladder run; with only a yaw
    // available (BoxInst has no pitch) that "slanting" run was simply a
    // vertical plank stuck out at an angle, so the whole thing read as a black
    // plus-sign glued to the wall — QA called them censor bars beside the
    // Apollo. It is now built the way the real thing is: a thin deck, a top and
    // mid rail carried on balusters (the facade shows BETWEEN the bars, which
    // is the entire read), and a vertical drop-ladder at alternating ends the
    // way NYC escapes actually stagger. Same instancing (one plain-box mesh,
    // no extra draw call), no rng touched — the gate draws above are unchanged.
    if (h > 6 && rng.next() < 0.55) {
      const [fx, fy, fz] = at(lat - w / 2 - 0.8);
      const [ox, , oz] = at(lat - w / 2 - 1.22); // the outboard rail line
      const iron = 0x1b1e24; // painted iron: dark, but it still catches an edge
      const run = w * 0.45; // along-course length of the deck
      const fwX = forwardX(s.dir);
      const fwZ = forwardZ(s.dir);
      const key = slotKey(s.distance, side);
      const bal = 4; // balusters per level
      let levels = 0;
      for (let lvl = 0; lvl < 3; lvl++) {
        const py = fy + h * (0.3 + lvl * 0.24);
        if (py > fy + h - 1) break;
        levels++;
        // The walking deck (thinner than the old 0.16 slab so the rails lead).
        a.plain.push({ x: fx, y: py, z: fz, rotY: s.dir, w: 0.92, h: 0.12, d: run, color: iron });
        // Top + mid rail, carried on the outboard edge.
        a.plain.push({ x: ox, y: py + 0.94, z: oz, rotY: s.dir, w: 0.1, h: 0.09, d: run, color: iron });
        a.plain.push({ x: ox, y: py + 0.52, z: oz, rotY: s.dir, w: 0.09, h: 0.07, d: run, color: iron });
        for (let b = 0; b < bal; b++) {
          const t = (b + 0.5) / bal - 0.5; // −0.5..0.5 along the deck
          a.plain.push({
            x: ox + fwX * t * run,
            y: py + 0.5,
            z: oz + fwZ * t * run,
            rotY: s.dir,
            w: 0.09,
            h: 0.98,
            d: 0.09,
            color: iron,
          });
        }
      }
      // The drop-ladders between decks: two stringers + rungs, hung at
      // alternating ends (hash-picked per building, no rng).
      const gap = h * 0.24;
      for (let lvl = 0; lvl < levels - 1; lvl++) {
        const end = (nycVary(key, lvl) < 0.5 ? -1 : 1) * run * 0.36;
        const py = fy + h * (0.3 + lvl * 0.24);
        for (const off of [-0.28, 0.28]) {
          a.plain.push({
            x: ox + fwX * (end + off),
            y: py + gap * 0.5,
            z: oz + fwZ * (end + off),
            rotY: s.dir,
            w: 0.08,
            h: gap,
            d: 0.08,
            color: iron,
          });
        }
        for (let r = 0; r < 3; r++) {
          a.plain.push({
            x: ox + fwX * end,
            y: py + gap * (0.25 + r * 0.25),
            z: oz + fwZ * end,
            rotY: s.dir,
            w: 0.07,
            h: 0.06,
            d: 0.62,
            color: iron,
          });
        }
      }
    }
    if (h > 9 && rng.next() < 0.45) {
      const [tx, ty, tz] = at(lat + rng.range(-1.5, 1.5));
      a.plain.push({ x: tx, y: ty + h + 0.2, z: tz, rotY: s.dir, w: 1.5, h: 1.1, d: 1.5, color: 0x2a241e }); // tower legs
      a.plain.push({ x: tx, y: ty + h + 1.3, z: tz, rotY: s.dir, w: 2.1, h: 2.0, d: 2.1, color: 0x5a4632 }); // the tank
      a.roofs.push({ x: tx, y: ty + h + 3.3, z: tz, rotY: s.dir, w: 2.5, h: 1.1, d: 2.5, color: 0x3c3026 }); // conical cap
    }
  }
  if (rng.next() < 0.22) {
    const [x, y, z] = at(hw + 2 + rng.range(0, 3));
    a.trees.push({ x, y, z, s: rng.range(0.7, 1.1), color: rng.pick(FOLIAGE) });
  }
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.4, hw + 3.6], chance: 0.4, count: 2 });
};

/**
 * CENTRAL PARK (nyc-lake round — the owner's CORRECTION of the previous round).
 *
 * The previous round read the M118 flag "should NYC's flat park apron BE water"
 * as yes and turned the whole apron into a welded reservoir. That was wrong:
 *
 *   "there was a misunderstanding about the 'water' in Central Park. The only
 *    water is supposed to be 'The Lake' that 'The Bow Bridge' is meant to cross
 *    over... Please revert the new water to grass and hills (texturized well)"
 *
 * So the apron is GRASS again, full width, and the rolling hills and the
 * distant skyline are back where they always were (both were withheld only
 * because they stood inside the reservoir). NYC emits ZERO course water.
 *
 * "Texturized well" is the part that is NOT a revert — the original M118
 * complaint (a flat dark slab) is real and reverting alone would restore it.
 * The fix is in the SURFACES, not the layout: the grass slabs carry a
 * world-anchored turf grain + bump (builders.buildFlat), and NYC's park domes
 * now render through a MEADOW treatment — a swelling, smooth-shaded dome with
 * a grass-tuft grain — instead of the plain faceted `HILL_GEO` sphere that was
 * literally why they read as flat kelly-green slabs (builders.buildHills).
 *
 * The park's small random ponds stay WITHHELD (draws intact): the owner's rule
 * is that The Lake is the only water in the park, and it is authored in
 * cities.ts `water.lakes` either side of Bow Bridge, plus the FORD that carries
 * it across the road under the bridge (the `ford` course feature).
 */
const park: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, step, progress, marks }) => {
  // Leave the ground bare right at the bridge so The Lake's water shows.
  const bridgeAt = marks.bowBridge;
  if (bridgeAt != null && Math.abs(progress - bridgeAt) < 0.02) return;
  // Expansive green: wide grass, rolling hills, dense trees — the skyline
  // pushed FAR back beyond it.
  {
    const w = rng.range(54, 84);
    const [x, y, z] = at(hw + w / 2 + 1);
    a.grass.push({
      x,
      y,
      z,
      rotY: s.dir,
      w,
      d: step * 2.2,
      color: rng.pick([0x2f7a36, 0x36863c, 0x3f9446, 0x2a7036]),
    });
  }
  const treeCount = rng.int(2, 5);
  for (let k = 0; k < treeCount; k++) {
    const lat = hw + 3 + rng.range(0, 50);
    const [x, y, z] = at(lat);
    const autumn = rng.next() < 0.16;
    a.trees.push({
      x,
      y,
      z,
      s: rng.range(0.9, 1.8),
      color: rng.pick(autumn ? FOLIAGE_AUTUMN : FOLIAGE),
    });
  }
  // Rolling green hills set back in the park (M98: the dome's edge is clamped
  // off the road — same rng draws, lateral adjusted afterwards).
  if (rng.next() < 0.32) {
    const base = hw + 20 + rng.range(0, 44);
    const r = rng.range(20, 42);
    const [x, y, z] = at(hillLat(hw, base, r));
    a.hills.push({ x, y, z, r, h: rng.range(5, 14), color: rng.pick(HILL_GREEN) });
  }
  // The park's small random ponds stay WITHHELD (owner: The Lake is the only
  // water in the park). Every draw still happens — the rng SEQUENCE is the
  // city, so only the push is dropped, the established byte-safe pattern. This
  // is also what retires the M117d works-site pond exclusion: with no park
  // ponds there is nothing left to flood the High Line excavator.
  if (rng.next() < 0.13) {
    rng.range(18, 30);
    rng.range(0, 8);
    rng.range(12, 20);
  }
  // Distant skyline FAR beyond the park (behind the hills).
  if (rng.next() < 0.16) {
    const w = rng.range(12, 20);
    const h = rng.range(26, 52);
    const [x, y, z] = at(hw + 82 + rng.range(0, 34));
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d: rng.range(12, 18), color: rng.pick(SKYLINE) });
  }
  // Park life: strollers near the path, picnickers sitting out on the lawn.
  scatterPeople({ peopleRng, at, a }, { band: [hw + 3, hw + 34], chance: 0.7, count: 3, sit: 0.45 });
};

/** The Gherkin stands on side -1 (cities.ts `landmarks`, at 0.8). */
const GHERKIN_SIDE = -1;
/**
 * Half-window (in progress) of the Gherkin forecourt clearing.
 *
 * MEASURED (live scene bbox → track coords, both seeds): the rebuilt lathe's
 * BODY is 32.1 across and its stone plinth deck 38.6, standing at lateral 36
 * (hw 18 + the def's offset 18) — so it occupies lateral 16.7-55.3 and reaches
 * 19.3 along the course either side of its arc. The City's towers spawn at
 * lateral 26.8-33.5 with half-widths 5-7.5, i.e. their footprints land INSIDE
 * the landmark's, 23-53 units tall in front of a 74-unit hero: measured, 15
 * towers stood within ±100 of it and every one of them crossed its band.
 *
 * 0.015 × the 2192-unit course = ±32.9 units, which covers the four 12-unit
 * placement steps that physically touch the plinth (da -19 / -7 / +5 / +17)
 * with a step of margin either side — the Gorée rule, sized to this footprint.
 */
const GHERKIN_CLEAR = 0.015;

/**
 * MIDTOWN FORECOURTS (nyc-park round, owner: "the Empire State isn't visible,
 * it's hidden behind other skyscrapers — bring it to the edge of the road so
 * it's prominent"). Same defect, same fix as the Gherkin, on New York's two
 * hero towers.
 *
 * MEASURED (live scene bbox → track coords, seed 123): the Chrysler occupies
 * arc ±26 about its mark and lateral 23.7-54.4; the Empire State arc ±26.5 and
 * lateral 22.7-64.7. This builder spawns generic glass towers at lateral 20-42
 * with half-widths 4.5-8 and heights 22-55 — i.e. their footprints sit INSIDE
 * both landmarks', in front of an 88 u and a 96 u hero. Nothing in NYC cleared
 * them, because `marks.gherkin` is London-only.
 *
 * 0.018 × the 2192 u course = ±39.5 u: it covers each tower's own ±26.5 reach
 * plus a full 12 u placement step of margin either side, so no generic tower
 * can straddle the forecourt edge and clip the hero. Both heroes stand on
 * side −1 (the driver's right), so only that side is withheld.
 *
 * A FORECOURT ALONE IS NOT ENOUGH, and this is the measured part. A driver
 * 130 u back sights the hero along a line that crosses lateral 19-32 over the
 * 70 u of road in front of it — exactly where the generic towers stand, 22-55
 * tall. Angularly a 55 u tower at 41 u range (1.34) buries a 96 u tower at
 * 131 u range (0.73): in landscape, at 1600, the Empire State was completely
 * hidden behind the ordinary street wall even WITH its forecourt cleared.
 *
 * Widening the discard to cover that would punch a ~110 u hole in Midtown. So
 * the approach gets the ST PAUL'S treatment instead (the precedent is six lines
 * up in this same builder): the towers stay — the canyon is the city — but they
 * are drawn LOW, so the hero rises clear over the roofline the whole way in.
 * One rng draw either way; the sequence never changes.
 */
const MIDTOWN_HERO_CLEAR = 0.018;
/** How far UP-COURSE of a hero the low-roofline approach reaches (0.05 ≈ 110 u
 *  — the sightline distance measured above, plus a step). */
const MIDTOWN_APPROACH = 0.05;
const MIDTOWN_HERO_SIDE = -1;

const skyscrapers: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, progress, marks }) => {
  // Give St Paul's a plaza: skip towers at the cathedral on its side, and
  // keep the opposite side's towers lower so the dome reads as the hero.
  const stPaulsAt = marks.stPauls;
  const nearStPauls = stPaulsAt != null && Math.abs(progress - stPaulsAt) < 0.03;
  if (nearStPauls && side === -1) return;
  // GHERKIN FORECOURT (london-ts). DISCARD, DON'T SKIP: unlike the St Paul's
  // plaza above (an M13-era `return` now baked into London's layout), every
  // rng draw below still happens inside this window — only the PUSH is
  // withheld on the landmark's side, so the rest of the city is byte-identical
  // and NYC's Broadway, which shares this builder, cannot be touched at all
  // (it has no gherkin landmark, so `marks.gherkin` is undefined there).
  // Crowds deliberately survive: people at a landmark's feet are the point
  // (the Gorée / Shibuya-109 rule).
  const gherkinAt = marks.gherkin;
  // The Midtown pair rides the SAME discard rule (draws happen, push withheld),
  // so London stays byte-identical and NYC only loses towers on the heroes' side.
  const heroes = side === MIDTOWN_HERO_SIDE ? [marks.chrysler, marks.empireState] : [];
  const midtownClear = heroes.some((at) => at != null && Math.abs(progress - at) < MIDTOWN_HERO_CLEAR);
  const midtownLow = heroes.some(
    (at) => at != null && at - progress > 0 && at - progress < MIDTOWN_APPROACH,
  );
  const clear =
    midtownClear ||
    (gherkinAt != null && side === GHERKIN_SIDE && Math.abs(progress - gherkinAt) < GHERKIN_CLEAR);
  if (rng.next() < 0.9) {
    const w = rng.range(9, 16);
    const h = nearStPauls ? rng.range(16, 30) : midtownLow ? rng.range(12, 26) : rng.range(22, 55);
    const d = rng.range(9, 16);
    const [x, y, z] = at(hw + 2 + w / 2 + rng.range(0, 6)); // tight canyon
    const color = rng.pick(GLASS); // drawn either way — the SEQUENCE is the city
    if (!clear) a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
  }
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.4, hw + 4.5], chance: 0.55, count: 3 });
};

/** Sign colours: the NEON palette with its pure white swapped for warm paper.
 *  On the sign material (MeshBasic, toneMapped:false) a 255-white panel blows
 *  out into a featureless slab — the trap the Tokyo round hit with its white
 *  glyph columns, and it is worse on a big board (measured here: one accent
 *  panel read as a hole punched in the facade). Same LENGTH as NEON on
 *  purpose, so the accent-neon draw below still picks the same slot. */
const SIGN_WHITE = 0xefe6d4;
const MARQUEE = NEON.map((c) => (c === 0xffffff ? SIGN_WHITE : c));

/** How many ad textures builders.adTextures() paints. The board picker spreads
 *  a facade's panels across the set so neighbours never repeat.
 *  shared-pass: the brand boards landed — 7 abstract patterns (indices 0-6,
 *  which is also all Tokyo's Shibuya screens can draw) plus 5 game-universe
 *  BRAND boards (DRIBBLE COLA, RACER ENERGY, TURBO, KICK STAR, NOVA TYRES) at
 *  7-11. MUST equal adTextures().length. */
const AD_TEX_COUNT = 12;

/** One panel on an ad facade, in facade-local units: `u` along the course from
 *  the facade centre, `yc` above the facade's base, `wid`/`hei` its size. */
interface AdBoard {
  u: number;
  yc: number;
  wid: number;
  hei: number;
  /** A flat lit colour band (a marquee/ticker) rather than a patterned screen. */
  band?: boolean;
}

/**
 * NYC TS round — TIMES SQUARE IS A MOSAIC, NOT A WRAP. Every tower used to
 * carry ONE screen covering its whole face, so the square read as a corridor of
 * flat colour test-patterns (QA: "placeholder art, not Times Square"). Real
 * Times Square is many boards of different sizes stacked and butted on one
 * facade, with lit marquee bands down at street level. Three layouts, picked by
 * index hash, tile the facade with gaps so the dark tower still reads between
 * them. Draw calls are UNCHANGED — buildAdScreens groups by texture, so panel
 * count is free.
 *
 * It also fixes a real geometry bug: the old screen's width came from the
 * building's LATERAL depth `w`, while buildAdScreens lays a panel's width ALONG
 * the course — on a deep, narrow tower the screen overhung its own building by
 * metres and floated. Boards are sized off `d` (the true street frontage) now.
 */
function adBoards(d: number, h: number, pattern: number): AdBoard[] {
  const U = d * 0.94; // usable frontage
  const H = h * 0.8; // usable height (base sits at 0.1h)
  if (pattern === 0) {
    // Hero board with a stacked pair beside it, over a full-width marquee.
    return [
      { u: -0.19 * U, yc: 0.67 * H, wid: 0.6 * U, hei: 0.64 * H },
      { u: 0.305 * U, yc: 0.81 * H, wid: 0.35 * U, hei: 0.36 * H },
      { u: 0.305 * U, yc: 0.46 * H, wid: 0.35 * U, hei: 0.24 * H },
      { u: 0, yc: 0.19 * H, wid: U, hei: 0.18 * H, band: true },
    ];
  }
  if (pattern === 1) {
    // A wide top board over two mid boards, marquee below.
    return [
      { u: 0, yc: 0.83 * H, wid: U, hei: 0.34 * H },
      { u: -0.27 * U, yc: 0.48 * H, wid: 0.44 * U, hei: 0.28 * H },
      { u: 0.255 * U, yc: 0.48 * H, wid: 0.47 * U, hei: 0.28 * H },
      { u: 0, yc: 0.19 * H, wid: U, hei: 0.22 * H, band: true },
    ];
  }
  // A tall corner column beside a big board and its shelf.
  return [
    { u: -0.335 * U, yc: 0.58 * H, wid: 0.31 * U, hei: 0.84 * H },
    { u: 0.175 * U, yc: 0.75 * H, wid: 0.63 * U, hei: 0.5 * H },
    { u: 0.175 * U, yc: 0.31 * H, wid: 0.63 * U, hei: 0.3 * H },
    { u: 0.175 * U, yc: 0.09 * H, wid: 0.63 * U, hei: 0.1 * H, band: true },
  ];
}

const timessquare: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side }) => {
  // The building IS the ad: dark towers wrapped in glowing screens on the road
  // face (distinct from Broadway's lit-window glass towers).
  if (rng.next() < 0.95) {
    const w = rng.range(11, 19);
    const h = rng.range(22, 50);
    const d = rng.range(8, 14);
    const lat = hw + 2 + w / 2 + rng.range(0, 4);
    const [x, y, z] = at(lat);
    a.adBuildings.push({ x, y, z, rotY: s.dir, w, h, d, color: rng.pick(ADBUILDING) });
    const [sx, , sz] = at(lat - w / 2 - 0.25);
    const lead = rng.int(0, 6); // 7 abstract ad textures (see builders.adTextures)
    // Everything below is index-hashed, never drawn from `rng` — the layout
    // sequence above is exactly what it always was.
    const key = slotKey(s.distance, side);
    const fwX = forwardX(s.dir);
    const fwZ = forwardZ(s.dir);
    const boards = adBoards(d, h, Math.floor(nycVary(key, 0) * 3) % 3);
    boards.forEach((b, bi) => {
      const bx = sx + fwX * b.u;
      const bz = sz + fwZ * b.u;
      const by = s.y + h * 0.1 + b.yc;
      if (b.band) {
        // The lit marquee band: a flat colour strip, the way the street-level
        // signage reads under the big screens.
        a.neon.push({
          x: bx,
          y: by,
          z: bz,
          rotY: s.dir,
          w: b.wid,
          h: b.hei,
          color: MARQUEE[Math.floor(nycVary(key, 9) * MARQUEE.length) % MARQUEE.length],
        });
        return;
      }
      // Spread the panels across the texture set so no two boards on a facade
      // (or on its neighbour) share a pattern.
      const tex = (lead + bi + Math.floor(nycVary(key, bi + 1) * (AD_TEX_COUNT - 1))) % AD_TEX_COUNT;
      a.adScreens.push({ x: bx, y: by, z: bz, rotY: s.dir, w: b.wid, h: b.hei, tex });
    });
  }
  // A few small accent neons between the big screens (MARQUEE, not NEON: same
  // palette, same length, same draw — only the blown-white slot is tamed).
  if (rng.next() < 0.45) {
    const [nx, , nz] = at(hw + 2.4);
    a.neon.push({
      x: nx,
      y: s.y + rng.range(6, 24),
      z: nz,
      rotY: s.dir,
      w: rng.range(4, 7),
      h: rng.range(4, 7),
      color: rng.pick(MARQUEE),
    });
  }
  // The Times Square throng.
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.4, hw + 6], chance: 0.85, count: 5 });
};

// ---- London ----------------------------------------------------------------

/**
 * Westminster door leaves (london-ts nit round). The old "glossy black door"
 * was ONE 0x15161a slab — and `plain` boxes are based at `y`, so a base of
 * `ground + 1.4` left it FLOATING a metre and a half up the wall: at close
 * range it read as a hole punched in the new Gothic facade, not a doorway
 * (QA: trees-kerb-close / palace-facade-close). It now sits on a threshold
 * inside a real stone doorcase, and the leaf takes a deep London colour rather
 * than pure black so it reads as joinery under any light. Index-hashed, never
 * rng — the city's draw sequence is untouched.
 */
const DOOR_LEAF = [0x24483a, 0x5a2226, 0x1f3350, 0x33363d]; // green / oxblood / navy / charcoal

const westminster: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side }) => {
  // Grand cream Portland-stone civic blocks with heavy cornices.
  if (rng.next() < 0.88) {
    const w = rng.range(9, 15);
    const h = rng.range(8, 18);
    const d = rng.range(10, 16);
    const lat = hw + 3 + w / 2 + rng.range(0, 6);
    const [x, y, z] = at(lat);
    const color = rng.pick(LONDON_STONE);
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
    a.plain.push({ x, y: y + h - 0.4, z, rotY: s.dir, w: w + 1, h: 1.2, d: d + 1, color: darken(color, 0.7) });
    // The London signatures (M14): brick CHIMNEY STACKS with clay pots on
    // every roofline, a white Georgian string-course band, a glossy black door.
    const stacks = rng.int(1, 2);
    for (let c = 0; c < stacks; c++) {
      const [cx, cy, cz] = at(lat + (c === 0 ? -w / 4 : w / 4));
      a.plain.push({ x: cx, y: cy + h + 0.9, z: cz, rotY: s.dir, w: 1.4, h: 1.8, d: 1.0, color: 0x7a5a48 }); // stack
      a.plain.push({ x: cx, y: cy + h + 2.1, z: cz, rotY: s.dir, w: 1.1, h: 0.7, d: 0.7, color: 0x53382c }); // the pots
    }
    a.plain.push({ x, y: y + h * 0.5, z, rotY: s.dir, w: w + 0.25, h: 0.4, d: d + 0.25, color: 0xefeada }); // string course
    // THE DOORCASE (see DOOR_LEAF): threshold step → a stone surround built as
    // TWO JAMBS AND A HEAD → the recessed leaf → a glazed fanlight → a cornice
    // hood. The surround MUST be a frame: shipped for one pass as a single
    // solid panel it simply hid the leaf behind it (measured in door-macro),
    // which is the same "blank slab" read the black door had. The lateral
    // offsets step OUT from the facade in order, so the leaf genuinely sits
    // back inside its jambs — that reveal is what gives it depth.
    const face = lat - w / 2;
    const fwX2 = forwardX(s.dir);
    const fwZ2 = forwardZ(s.dir);
    const [sx3, gy, sz3] = at(face - 0.3); // threshold step
    const [ax3, , az3] = at(face - 0.11); // jambs + head
    const [lx3, , lz3] = at(face - 0.05); // leaf (recessed inside them)
    const [hx3, , hz3] = at(face - 0.19); // hood
    const LEAF_H = 2.4;
    const STEP_H = 0.2;
    const CASE_H = LEAF_H + 1.0;
    const OPEN = 1.4; // clear opening along the course
    const JAMB = 0.26;
    a.plain.push({ x: sx3, y: gy, z: sz3, rotY: s.dir, w: 0.6, h: STEP_H, d: OPEN + 0.45, color: 0xd9d1bb });
    for (const sgn of [-1, 1]) {
      const j = sgn * (OPEN + JAMB) * 0.5;
      a.plain.push({ x: ax3 + fwX2 * j, y: gy, z: az3 + fwZ2 * j, rotY: s.dir, w: 0.22, h: CASE_H, d: JAMB, color: 0xe4dcc6 });
    }
    a.plain.push({ x: ax3, y: gy + CASE_H - 0.34, z: az3, rotY: s.dir, w: 0.22, h: 0.34, d: OPEN + 2 * JAMB, color: 0xe4dcc6 }); // head
    a.plain.push({ x: lx3, y: gy + STEP_H, z: lz3, rotY: s.dir, w: 0.16, h: LEAF_H, d: OPEN, color: DOOR_LEAF[Math.floor(nycVary(slotKey(s.distance, side), 41) * DOOR_LEAF.length) % DOOR_LEAF.length] });
    a.plain.push({ x: lx3, y: gy + STEP_H + LEAF_H + 0.06, z: lz3, rotY: s.dir, w: 0.14, h: 0.4, d: OPEN - 0.06, color: 0xe8dcc0 }); // fanlight glazing
    a.plain.push({ x: hx3, y: gy + CASE_H, z: hz3, rotY: s.dir, w: 0.44, h: 0.28, d: OPEN + 2 * JAMB + 0.34, color: 0xcfc5ab }); // cornice hood
  }
  if (rng.next() < 0.2) {
    const [x, y, z] = at(hw + 2 + rng.range(0, 3));
    a.trees.push({ x, y, z, s: rng.range(0.9, 1.3), color: rng.pick(FOLIAGE) });
  }
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.5, hw + 4.5], chance: 0.5, count: 3 });
};

/**
 * shared-2b: how far past the guardrail the ground apron reaches.
 * KEEP IN SYNC with `GROUND_EXTEND` in world/TrackMesh.ts — it is not
 * exported, and the ground sheet simply STOPS at hw + this. Anything placed
 * beyond it stands on nothing.
 */
const GROUND_EXTENT = 80;
/** Cushion inside that edge, so a block's outer face never grazes it. */
const FAR_BANK_MARGIN = 2;
/** How far a clamped far-bank block may still be scattered back in-course, so
 *  the whole skyline doesn't collapse onto one dead-straight rank. */
const FAR_BANK_SCATTER = 11;

/**
 * Clamp a Thames far-bank block so its WHOLE footprint stays on the ground.
 *
 * MEASURED (both seeds): `thames` wants these at lateral hw+72..hw+98 and
 * they are up to 18 wide, so their outer faces reach hw+107 — but the ground
 * apron ends at hw+80. 34 of 36 blocks on the tour seed (31 of 36 on seed
 * 123) hung past that edge, worst overhang 25.4u, and one is visibly FLOATING
 * over the river in the london-ts finish-elevated shot: past the apron the
 * terrain the water clamps itself to belongs to another limb of the course,
 * so the block's own road-grade `y` leaves it in mid-air.
 *
 * The rng draws are untouched — only the RESULT moves (the `hillLat`
 * pattern). `jitter` is an index-derived 0..1 (never rng), which keeps the
 * far bank's depth variety once the clamp bites.
 */
function farBankLat(hw: number, want: number, w: number, jitter: number): number {
  const cap = hw + GROUND_EXTENT - FAR_BANK_MARGIN - w / 2;
  return want <= cap ? want : cap - jitter * FAR_BANK_SCATTER;
}

const thames: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, step }) => {
  if (side === RIVER_SIDE) {
    // The open river: a wide water strip beyond the rail, tiled continuously.
    const w = rng.range(54, 70);
    const [x, y, z] = at(hw + w / 2 + 1);
    a.ponds.push({ x, y, z, rotY: s.dir, w, d: step * 2.2, color: 0x274f63, arc: s.distance, side, shore: hw + 1 });
    // A hazy low skyline on the far bank.
    if (rng.next() < 0.4) {
      const bw = rng.range(10, 18);
      const bh = rng.range(10, 28);
      const want = hw + 72 + rng.range(0, 26);
      const [bx, by, bz] = at(farBankLat(hw, want, bw, nycVary(slotKey(s.distance, side), 61)));
      a.buildings.push({ x: bx, y: by, z: bz, rotY: s.dir, w: bw, h: bh, d: rng.range(10, 16), color: rng.pick(SKYLINE) });
    }
  } else {
    // Embankment side: a continuous low stone river wall, plane trees, set-back blocks.
    //
    // london-ts nit round — THIS is what QA read as "untextured cream slabs" at
    // the finish (it runs right behind the grandstands on the Tower Bridge
    // approach, and beside TrackMesh's painted guardrail a bare box looks like
    // an unfinished plinth). It was ONE flat-colour 1.6×2.4 box per step: no
    // surface, no profile, and butted end to end into a single blank mass.
    // Now it is a real Thames parapet — plinth course, rusticated body,
    // smooth granite coping — with a PIER at every segment head, because the
    // rhythm is what stops a continuous run reading as one slab. The body,
    // plinth and pier colours are in builders.LONDON_MASONRY, which routes them
    // to the stone-grained plain mesh; the copings stay smooth on purpose
    // (granite cap vs coursed wall is the whole material contrast).
    const [wx, wy, wz] = at(hw + 1.2);
    const [px3, , pz3] = at(hw + 1.2);
    const segD = step + 1;
    a.plain.push({ x: px3, y: wy, z: pz3, rotY: s.dir, w: 1.75, h: 0.42, d: segD, color: 0x9c9479 }); // plinth course
    a.plain.push({ x: wx, y: wy, z: wz, rotY: s.dir, w: 1.35, h: 1.95, d: segD, color: 0xb9b095 }); // body
    a.plain.push({ x: wx, y: wy + 1.95, z: wz, rotY: s.dir, w: 1.62, h: 0.34, d: segD, color: 0xc9c1ab }); // coping
    const qx = wx + forwardX(s.dir) * (segD / 2);
    const qz = wz + forwardZ(s.dir) * (segD / 2);
    a.plain.push({ x: qx, y: wy, z: qz, rotY: s.dir, w: 1.9, h: 2.5, d: 1.3, color: 0xaaa287 }); // pier
    a.plain.push({ x: qx, y: wy + 2.5, z: qz, rotY: s.dir, w: 2.08, h: 0.3, d: 1.5, color: 0xc9c1ab }); // pier cap
    if (rng.next() < 0.4) {
      const [x, y, z] = at(hw + 3 + rng.range(0, 3));
      a.trees.push({ x, y, z, s: rng.range(0.9, 1.4), color: rng.pick(FOLIAGE) });
    }
    if (rng.next() < 0.5) {
      const bw = rng.range(9, 14);
      const bh = rng.range(8, 20);
      const [x, y, z] = at(hw + 8 + bw / 2 + rng.range(0, 6));
      a.buildings.push({ x, y, z, rotY: s.dir, w: bw, h: bh, d: rng.range(10, 15), color: rng.pick(LONDON_STONE) });
    }
    // Strollers along the embankment walk (never out over the river).
    scatterPeople({ peopleRng, at, a }, { band: [hw + 2.4, hw + 6], chance: 0.4, count: 2 });
  }
};

// ---- Fallback ---------------------------------------------------------------

const generic: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, theme }) => {
  // Generic themed cityscape (cities awaiting their own route).
  if (rng.next() < 0.8) {
    const w = rng.range(7, 14);
    const h = rng.range(6, 24);
    const d = rng.range(8, 15);
    const tinted = rng.next() < 0.18;
    const [x, y, z] = at(hw + 3 + w / 2 + rng.range(0, 12));
    a.buildings.push({
      x,
      y,
      z,
      rotY: s.dir,
      w,
      h,
      d,
      color: tinted ? theme.wall : rng.pick(GENERIC),
    });
  }
  if (rng.next() < 0.12) {
    const [x, y, z] = at(hw + 4 + rng.range(0, 8));
    a.trees.push({ x, y, z, s: rng.range(0.9, 1.4), color: rng.pick(FOLIAGE) });
  }
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.6, hw + 5], chance: 0.3, count: 2 });
};

export const ZONE_BUILDERS: Record<SceneryKind, ZoneBuilder> = {
  brownstones,
  park,
  skyscrapers,
  timessquare,
  westminster,
  thames,
  ...NAPOLI_ZONES,
  ...TOKYO_ZONES,
  ...RIO_ZONES,
  ...DAKAR_ZONES,
  ...LAGOS_ZONES,
  ...MUMBAI_ZONES,
  ...PATAGONIA_ZONES,
  generic,
};
